import { useTranslations } from '@/i18n/use-translations';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Collapse, IconButton, Typography } from '@mui/material';
import { useState, useRef, useEffect } from 'react';

export default function ExpandableDescription({ description: fullDescription }: { description: string }) {
  const [expanded, setExpanded] = useState(false);
  const [needsExpand, setNeedsExpand] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const t = useTranslations();

  const description = fullDescription?.length > 0 ? fullDescription : t('common.N/A');

  useEffect(() => {
    if (measureRef.current) {
      const isWrapped = measureRef.current.scrollHeight > 24; // 24px is the collapsedSize
      setNeedsExpand(isWrapped);
    }
  }, [description]);

  const handleExpand = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setExpanded(!expanded);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Hidden element to measure full text height */}
      <Box
        ref={measureRef}
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          width: '100%',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          wordWrap: 'break-word',
        }}
      >
        <Typography variant="body2">{description}</Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: needsExpand ? `calc(100% - 28px)` : '100%', // Only account for button width if needed
          }}
        >
          <Typography
            component="div"
            variant="body2"
            ref={textRef}
            sx={{
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
              wordBreak: 'break-word',
              wordWrap: 'break-word',
            }}
          >
            <Collapse in={expanded} collapsedSize="24px" sx={{ width: '100%' }}>
              {description}
            </Collapse>
          </Typography>
        </Box>
        {needsExpand && (
          <IconButton
            size="small"
            onClick={(e) => handleExpand(e)}
            sx={{
              mt: -0.5,
              flexShrink: 0,
              width: '24px',
              height: '24px',
            }}
          >
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
